import { rem } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { minutesToHm } from '@reditelji/shared';
import type { ExternalGroupRow, ExternalSlotRow } from '../api/externalGroups';
import { useExternalGroupMutations, type ExternalGroupInput } from '../api/externalGroups';
import { useCommitField, type CommitState } from '../lib/draft';
import type { PostRow } from '../api/posts';
import { useSession } from '../api/session';
import type { EventDay, Shift } from '../api/types';
import { plOs, plS } from '../lib/ui';
import { hoursLabel, rollupFor, type VolunteerRollup } from './derive';
import { CARD, INPUT, MONO, SECTION_LABEL } from './ui';

/** The square that marks an external team everywhere in this app (SPEC §1.1J). */
function Sq({ dim }: { dim?: boolean }) {
  return (
    <span
      aria-hidden
      style={{
        width: 7,
        height: 7,
        borderRadius: 1,
        flexShrink: 0,
        background: dim ? 'oklch(0.78 0.008 95)' : 'oklch(0.58 0.014 90)',
      }}
    />
  );
}

/** Plain text a coordinator can paste into a mail or message to the leader. */
function summaryText(g: ExternalGroupRow, rollups: Map<string, VolunteerRollup>): string {
  const lines: string[] = [g.name];
  if (g.leaderName || g.leaderPhone) {
    lines.push(`Vodja: ${[g.leaderName, g.leaderPhone].filter(Boolean).join(' · ')}`);
  }
  lines.push('');
  for (const slot of g.slots) {
    const r = rollups.get(slot.id);
    const who = slot.externalPersonName ? `${slot.name} — ${slot.externalPersonName}` : slot.name;
    lines.push(who);
    if (!r || r.shifts.length === 0) {
      lines.push('  brez izmen');
      continue;
    }
    for (const s of r.shifts) {
      const meet = s.meetMin !== null ? ` · zbor ${minutesToHm(s.meetMin)}` : '';
      lines.push(
        `  ${s.dayLabel} · ${s.postCode}${s.postMicro ? ` · ${s.postMicro}` : ''} · ${minutesToHm(
          s.fromMin,
        )}–${minutesToHm(s.toMin)}${meet}`,
      );
    }
  }
  return lines.join('\n');
}

/** What a field that saves on its own says while it is not simply saved. */
const COMMIT_TITLE: Record<CommitState, string | undefined> = {
  idle: undefined,
  saving: 'Shranjujem …',
  error: 'Ni shranjeno — popravi ali poskusi znova.',
};
const ERROR_INK = 'oklch(0.55 0.14 25)';

function Field({
  label,
  value,
  save,
  accept,
  mono,
  placeholder,
  readOnly,
}: {
  label: string;
  value: string;
  save: (text: string) => Promise<unknown>;
  accept?: (text: string) => boolean;
  mono?: boolean;
  placeholder?: string;
  /** Text instead of an input: a field that commits on blur is never mounted to be looked at. */
  readOnly?: boolean;
}) {
  const { state, inputProps } = useCommitField({ value, save, accept });
  if (readOnly) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 104, flexShrink: 0, fontSize: rem(12), color: 'oklch(0.62 0.012 90)' }}>
          {label}
        </div>
        <div style={{ flex: 1, minWidth: 0, fontSize: rem(13), fontFamily: mono ? MONO : undefined }}>
          {value || placeholder || '—'}
        </div>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ width: 104, flexShrink: 0, fontSize: rem(12), color: 'oklch(0.62 0.012 90)' }}>
        {label}
      </div>
      <input
        {...inputProps}
        placeholder={placeholder}
        title={COMMIT_TITLE[state]}
        aria-invalid={state === 'error' || undefined}
        style={{
          ...INPUT,
          flex: 1,
          minWidth: 0,
          fontFamily: mono ? MONO : 'var(--rr-font)',
          background: 'oklch(0.99 0.003 95)',
          borderColor: state === 'error' ? ERROR_INK : undefined,
          opacity: state === 'saving' ? 0.7 : 1,
        }}
      />
    </div>
  );
}

/**
 * One external team: its leader, how many people it sends, and what they cover.
 *
 * No mail action anywhere on this screen — the app never writes to a leader
 * (SPEC §1.1J). The copy button exists so a coordinator can send the schedule
 * themselves, in whatever channel that team actually uses.
 */
export function TeamDetail({
  group,
  days,
  shifts,
  postById,
  onDeleted,
}: {
  group: ExternalGroupRow;
  days: EventDay[];
  shifts: Shift[];
  postById: Map<string, PostRow>;
  onDeleted: () => void;
}) {
  const mut = useExternalGroupMutations();
  // A team is part of the plan: the organisation reads it (SPEC §1.1L).
  const canEdit = useSession().can('plan:write');
  const [confirmDelete, setConfirmDelete] = useState(false);
  useEffect(() => setConfirmDelete(false), [group.id]);

  const rollups = new Map<string, VolunteerRollup>(
    group.slots.map((s) => [s.id, rollupFor(s, shifts, days, postById)]),
  );
  const totalMin = [...rollups.values()].reduce((a, r) => a + r.minutes, 0);
  const shiftCount = [...rollups.values()].reduce((a, r) => a + r.shifts.length, 0);

  // Resolves once the server holds it; `useExternalGroupMutations` orders the
  // writes and carries the `updatedAt` each one needs.
  const patch = (p: Partial<ExternalGroupInput>) => mut.update.mutateAsync({ id: group.id, ...p });

  const setCount = (n: number) => {
    if (n < 0 || n > 50) return;
    mut.setSlots.mutate({ id: group.id, count: n });
  };

  return (
    <div style={{ padding: '18px 22px 40px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: rem(20), fontWeight: 700, color: 'oklch(0.25 0.012 90)' }}>
            {group.name}
          </div>
          <div style={{ fontSize: rem(12.5), color: 'oklch(0.5 0.012 90)', marginTop: 3 }}>
            Zunanja ekipa · {plOs(group.slots.length)}
            {shiftCount ? ` · ${plS(shiftCount)}` : ''} · {hoursLabel(totalMin)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard.writeText(summaryText(group, rollups));
              notifications.show({ message: 'Razpored kopiran', color: 'green' });
            }}
            style={{
              cursor: 'pointer',
              padding: '6px 11px',
              borderRadius: 7,
              border: '1px solid oklch(0.85 0.05 245)',
              background: 'oklch(0.965 0.025 245)',
              color: 'oklch(0.4 0.08 245)',
              fontSize: rem(12.5),
              fontWeight: 600,
              fontFamily: 'inherit',
            }}
          >
            Kopiraj razpored
          </button>
          {!canEdit ? null : confirmDelete ? (
            <>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                style={{
                  cursor: 'pointer',
                  padding: '6px 11px',
                  borderRadius: 7,
                  border: '1px solid oklch(0.91 0.006 95)',
                  background: 'oklch(1 0 0)',
                  fontSize: rem(12.5),
                  fontFamily: 'inherit',
                }}
              >
                Prekliči
              </button>
              <button
                type="button"
                onClick={() => mut.remove.mutate(group.id, { onSuccess: onDeleted })}
                style={{
                  cursor: 'pointer',
                  padding: '6px 11px',
                  borderRadius: 7,
                  border: '1px solid oklch(0.75 0.12 25)',
                  background: 'oklch(0.955 0.045 25)',
                  color: 'oklch(0.42 0.12 25)',
                  fontSize: rem(12.5),
                  fontWeight: 600,
                  fontFamily: 'inherit',
                }}
              >
                Res izbriši
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              style={{
                cursor: 'pointer',
                padding: '6px 11px',
                borderRadius: 7,
                border: '1px solid oklch(0.91 0.006 95)',
                background: 'oklch(1 0 0)',
                color: 'oklch(0.5 0.09 25)',
                fontSize: rem(12.5),
                fontFamily: 'inherit',
              }}
            >
              Izbriši ekipo
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14 }}>
        <div style={{ ...CARD, padding: '14px 16px' }}>
          <div style={{ ...SECTION_LABEL, fontWeight: 700, marginBottom: 11 }}>Vodja</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            <Field
              readOnly={!canEdit}
              label="Ime ekipe"
              value={group.name}
              accept={(text) => text !== ''}
              save={(name) => patch({ name })}
            />
            <Field
              readOnly={!canEdit}
              label="Ime in priimek"
              value={group.leaderName}
              save={(leaderName) => patch({ leaderName })}
            />
            <Field
              readOnly={!canEdit}
              label="E-pošta"
              value={group.leaderEmail ?? ''}
              placeholder="—"
              save={(email) => patch({ leaderEmail: email || null })}
            />
            <Field
              readOnly={!canEdit}
              label="Telefon"
              value={group.leaderPhone}
              mono
              save={(leaderPhone) => patch({ leaderPhone })}
            />
          </div>
          <div
            style={{
              marginTop: 11,
              paddingTop: 10,
              borderTop: '1px solid oklch(0.95 0.005 95)',
              fontSize: rem(11.5),
              color: 'oklch(0.62 0.012 90)',
              lineHeight: 1.45,
            }}
          >
            Vodji ne pošiljamo pošte iz aplikacije. Njegov telefon se pokaže na Predajah in v živo
            na dan dogodka.
          </div>
        </div>

        <div style={{ ...CARD, padding: '14px 16px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ ...SECTION_LABEL, fontWeight: 700, marginBottom: 11 }}>Osebe</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{ width: 104, flexShrink: 0, fontSize: rem(12), color: 'oklch(0.62 0.012 90)' }}
            >
              Število oseb
            </div>
            {canEdit ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: 6,
                  border: '1px solid oklch(0.93 0.006 95)',
                  overflow: 'hidden',
                }}
              >
                <button
                  type="button"
                  aria-label="Manj oseb"
                  onClick={() => setCount(group.slots.length - 1)}
                  disabled={mut.setSlots.isPending || group.slots.length === 0}
                  style={{
                    cursor: 'pointer',
                    padding: '6px 11px',
                    border: 0,
                    background: 'oklch(0.98 0.004 95)',
                    color: 'oklch(0.5 0.012 90)',
                    fontSize: rem(14),
                  }}
                >
                  −
                </button>
                <div
                  style={{
                    padding: '6px 14px',
                    fontFamily: MONO,
                    fontSize: rem(13),
                    fontWeight: 600,
                    borderLeft: '1px solid oklch(0.94 0.005 95)',
                    borderRight: '1px solid oklch(0.94 0.005 95)',
                  }}
                >
                  {group.slots.length}
                </div>
                <button
                  type="button"
                  aria-label="Več oseb"
                  onClick={() => setCount(group.slots.length + 1)}
                  disabled={mut.setSlots.isPending}
                  style={{
                    cursor: 'pointer',
                    padding: '6px 11px',
                    border: 0,
                    background: 'oklch(0.98 0.004 95)',
                    color: 'oklch(0.5 0.012 90)',
                    fontSize: rem(14),
                  }}
                >
                  +
                </button>
              </div>
            ) : (
              <div style={{ fontFamily: MONO, fontSize: rem(13), fontWeight: 600 }}>{group.slots.length}</div>
            )}
          </div>
          <div
            style={{
              marginTop: 11,
              fontSize: rem(11.5),
              color: 'oklch(0.62 0.012 90)',
              lineHeight: 1.45,
            }}
          >
            Aplikacija sama poimenuje osebe{' '}
            <span
              style={{
                fontFamily: MONO,
                fontSize: rem(11),
                background: 'oklch(0.96 0.005 95)',
                padding: '1px 4px',
                borderRadius: 3,
              }}
            >
              {group.name} 1–{Math.max(group.slots.length, 1)}
            </span>
            . Preimenovanje ekipe preimenuje vse.
          </div>
        </div>
      </div>

      <div style={{ ...CARD, overflow: 'hidden' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '11px 16px',
            borderBottom: '1px solid oklch(0.94 0.005 95)',
          }}
        >
          <div style={{ ...SECTION_LABEL, fontWeight: 700 }}>Razpored</div>
          <div style={{ fontSize: rem(11.5), color: 'oklch(0.62 0.012 90)' }}>
            {plOs(group.slots.length)} · {plS(shiftCount)} ·{' '}
            <span style={{ fontFamily: MONO }}>{hoursLabel(totalMin)}</span>
          </div>
        </div>

        {group.slots.length === 0 ? (
          <div style={{ padding: '18px 16px', fontSize: rem(12.5), color: 'oklch(0.6 0.012 90)' }}>
            Ekipa še nima oseb. Nastavi število oseb zgoraj in jih dodeli na Časovnici.
          </div>
        ) : (
          group.slots.map((slot) => (
            <SlotRow
              key={slot.id}
              groupId={group.id}
              slot={slot}
              rollup={rollups.get(slot.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function SlotRow({
  groupId,
  slot,
  rollup,
}: {
  groupId: string;
  slot: ExternalSlotRow;
  rollup: VolunteerRollup | undefined;
}) {
  const mut = useExternalGroupMutations();
  const canEdit = useSession().can('plan:write');
  const shifts = rollup?.shifts ?? [];
  const person = useCommitField({
    value: slot.externalPersonName,
    save: (externalPersonName) =>
      mut.updateSlot.mutateAsync({ id: groupId, slotId: slot.id, externalPersonName }),
  });
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 14,
        padding: '11px 16px',
        borderTop: '1px solid oklch(0.96 0.004 95)',
      }}
    >
      <div style={{ width: 176, flexShrink: 0, display: 'flex', alignItems: 'flex-start', gap: 7 }}>
        <span style={{ marginTop: 5 }}>
          <Sq dim={shifts.length === 0} />
        </span>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: rem(13),
              fontWeight: 600,
              color: shifts.length ? undefined : 'oklch(0.52 0.012 90)',
            }}
          >
            {slot.name}
          </div>
          {canEdit ? (
            <input
              {...person.inputProps}
              placeholder="ime osebe (neobvezno)"
              aria-label={`Ime osebe za ${slot.name}`}
              title={COMMIT_TITLE[person.state]}
              aria-invalid={person.state === 'error' || undefined}
              style={{
                border: 0,
                borderBottom: `1px solid ${person.state === 'error' ? ERROR_INK : 'transparent'}`,
                outline: 'none',
                padding: 0,
                width: '100%',
                background: 'transparent',
                fontFamily: 'inherit',
                fontSize: rem(11.5),
                color: person.state === 'error' ? ERROR_INK : 'oklch(0.6 0.012 90)',
                opacity: person.state === 'saving' ? 0.7 : 1,
              }}
            />
          ) : slot.externalPersonName ? (
            <div style={{ fontSize: rem(11.5), color: 'oklch(0.6 0.012 90)' }}>{slot.externalPersonName}</div>
          ) : null}
        </div>
      </div>

      {shifts.length === 0 ? (
        <div style={{ fontSize: rem(12.5), color: 'oklch(0.62 0.012 90)', paddingTop: 2 }}>
          brez izmen
        </div>
      ) : (
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 5 }}>
          {shifts.map((s) => (
            <div
              key={s.id}
              style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: rem(12.5) }}
            >
              <span style={{ width: 34, flexShrink: 0, color: 'oklch(0.62 0.012 90)' }}>
                {s.dayLabel.slice(0, 3).toLowerCase()}
              </span>
              <span
                style={{
                  fontFamily: MONO,
                  fontSize: rem(11.5),
                  fontWeight: 700,
                  color: 'oklch(0.35 0.012 90)',
                }}
              >
                {s.postCode}
              </span>
              <span
                style={{
                  color: 'oklch(0.5 0.012 90)',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {s.postMicro}
              </span>
              <span
                style={{
                  marginLeft: 'auto',
                  flexShrink: 0,
                  fontFamily: MONO,
                  fontSize: rem(11.5),
                  color: s.clash ? 'oklch(0.45 0.12 25)' : 'oklch(0.45 0.012 90)',
                }}
              >
                {minutesToHm(s.fromMin)}–{minutesToHm(s.toMin)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
